import React, { useState, useEffect } from 'react'
import { Input } from './Input'
import { UnitSelector, useUnitInfo, formatValueWithUnit } from './UnitSelector'
import { apiClient } from '@/services/api'

interface ConversionResult {
  originalValue: number
  originalUnit: string
  convertedValue: number
  convertedUnit: string
  conversionFactor: number
}

interface QuantityInputProps {
  value: number
  unit: string
  onValueChange: (value: number) => void
  onUnitChange: (unit: string) => void
  baseUnit?: string // Show conversion to base unit
  availableStock?: number // Show available stock for validation
  stockUnit?: string // Unit of available stock
  category?: string // Filter units by category
  recommendedUnits?: string[]
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  showConversion?: boolean // Show conversion preview
  className?: string
}

export const QuantityInput: React.FC<QuantityInputProps> = ({
  value,
  unit,
  onValueChange,
  onUnitChange,
  baseUnit,
  availableStock,
  stockUnit,
  category,
  recommendedUnits,
  label,
  placeholder = "Enter quantity...",
  required = false,
  disabled = false,
  showConversion = true,
  className
}) => {
  const [conversion, setConversion] = useState<ConversionResult | null>(null)
  const [conversionLoading, setConversionLoading] = useState(false)
  const [stockValidation, setStockValidation] = useState<{
    isValid: boolean
    message?: string
    convertedUsage?: number
  } | null>(null)

  const { unitInfo } = useUnitInfo(unit)

  // Update conversion when value or unit changes
  useEffect(() => {
    if (value && unit && baseUnit && unit !== baseUnit && showConversion) {
      fetchConversion()
    } else {
      setConversion(null)
    }
  }, [value, unit, baseUnit, showConversion])

  // Validate against available stock
  useEffect(() => {
    if (value && unit && availableStock !== undefined && stockUnit) {
      validateStock()
    } else {
      setStockValidation(null)
    }
  }, [value, unit, availableStock, stockUnit])

  const fetchConversion = async () => {
    if (!value || !unit || !baseUnit || unit === baseUnit) return

    setConversionLoading(true)
    try {
      const response = await apiClient.post('/materials/convert-units', {
        value,
        fromUnit: unit,
        toUnit: baseUnit
      })
      setConversion(response)
    } catch (error) {
      console.error('Error converting units:', error)
      setConversion(null)
    } finally {
      setConversionLoading(false)
    }
  }

  const validateStock = async () => {
    if (!value || !unit || availableStock === undefined || !stockUnit) return

    try {
      // Convert usage to stock unit for comparison
      let convertedUsage = value
      if (unit !== stockUnit) {
        const response = await apiClient.post('/materials/convert-units', {
          value,
          fromUnit: unit,
          toUnit: stockUnit
        })
        convertedUsage = response.convertedValue
      }

      const isValid = convertedUsage <= availableStock
      setStockValidation({
        isValid,
        convertedUsage,
        message: isValid 
          ? undefined 
          : `Insufficient stock. Available: ${formatValueWithUnit(availableStock, stockUnit)}`
      })
    } catch (error) {
      console.error('Error validating stock:', error)
      setStockValidation(null)
    }
  }

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value) || 0
    onValueChange(newValue)
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="flex space-x-2">
        <div className="flex-1">
          <Input
            type="number"
            value={value || ''}
            onChange={handleValueChange}
            placeholder={placeholder}
            required={required}
            disabled={disabled}
            className={stockValidation?.isValid === false ? 'border-red-500' : ''}
            min="0"
            step="any"
          />
        </div>
        
        <div className="w-48">
          <UnitSelector
            value={unit}
            onChange={onUnitChange}
            category={category}
            recommendedUnits={recommendedUnits}
            required={required}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Conversion Preview */}
      {showConversion && conversion && (
        <div className="text-sm text-gray-600 bg-blue-50 p-2 rounded">
          <div className="flex items-center space-x-2">
            <span>📐</span>
            <span>
              {formatValueWithUnit(conversion.originalValue, conversion.originalUnit)} = {' '}
              <strong>{formatValueWithUnit(conversion.convertedValue, conversion.convertedUnit)}</strong>
            </span>
          </div>
        </div>
      )}

      {/* Loading Conversion */}
      {conversionLoading && (
        <div className="text-sm text-gray-500 bg-gray-50 p-2 rounded">
          <div className="flex items-center space-x-2">
            <div className="animate-spin h-3 w-3 border border-gray-400 border-t-transparent rounded-full"></div>
            <span>Converting units...</span>
          </div>
        </div>
      )}

      {/* Stock Validation */}
      {stockValidation && (
        <div className={`text-sm p-2 rounded ${
          stockValidation.isValid 
            ? 'text-green-700 bg-green-50' 
            : 'text-red-700 bg-red-50'
        }`}>
          <div className="flex items-center space-x-2">
            <span>{stockValidation.isValid ? '✅' : '⚠️'}</span>
            <span>
              {stockValidation.isValid ? (
                <>
                  Stock available: {formatValueWithUnit(availableStock!, stockUnit!)}
                  {stockValidation.convertedUsage !== value && stockValidation.convertedUsage !== undefined && (
                    <span className="text-gray-600">
                      {' '}(using {formatValueWithUnit(stockValidation.convertedUsage, stockUnit!)})
                    </span>
                  )}
                </>
              ) : (
                stockValidation.message
              )}
            </span>
          </div>
        </div>
      )}

      {/* Unit Information */}
      {unitInfo && (
        <div className="text-xs text-gray-500">
          {unitInfo.name} ({unitInfo.symbol}) - {unitInfo.category}
        </div>
      )}
    </div>
  )
}

// Simplified version for basic quantity input without conversion
export const SimpleQuantityInput: React.FC<{
  value: number
  onChange: (value: number) => void
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  className?: string
}> = ({ value, onChange, label, placeholder, required, disabled, className }) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value) || 0
    onChange(newValue)
  }

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <Input
        type="number"
        value={value || ''}
        onChange={handleChange}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        min="0"
        step="any"
      />
    </div>
  )
}